import type { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { canAccessDevHarness, shouldLandOnDevHarness, signedInHomeRoute } from '@/utils/env'
import { isDesktopShell, isNativePlatform } from '@/utils/platform'
import { isCompanionApp } from '@/native/companion'
import { companionRedirectFor } from '@/native/companionRoutes'

/**
 * Native app v1 scope is auth + dashboard only. Marketing/landing pages
 * and the (desktop-only) guest demo tour are excluded —
 * the App Store listing covers marketing, and the tour is deferred.
 * Redirect straight to the dashboard (or login, if unauthenticated) instead
 * of forking the route table.
 */
const NATIVE_EXCLUDED_ROUTE_NAMES = new Set([
    'home',
    'launch',
    'launch-signup',
    'guest-tour',
    'pricing-guide',
    'our-story',
    'press',
    'ambassador',
    'partners-landing',
    'partner-portal',
    'partner-login',
    'partner-auth',
    'inbox-landing',
    'deals-landing',
    'assistant-landing',
    'changelog',
    'updates',
    'update-detail',
])

export function authGuard(to: RouteLocationNormalized, _from: RouteLocationNormalized): NavigationGuardReturn {
    const authStore = useAuthStore()
    const native = isNativePlatform()
    // Desktop shell shares the native route scope: product only, no marketing site.
    const appShell = native || isDesktopShell()

    if (appShell && to.name === 'native-login' && authStore.isAuthenticated) {
        return signedInHomeRoute(authStore.currentUser?.email)
    }

    // Store builds (and `?companion=1` QA): a strict allowlist; marketing, /verify, pricing, referrals,
    // demos and internal tools go to the dashboard (or sign-in). See src/native/companionRoutes.ts.
    if (isCompanionApp()) {
        const redirect = companionRedirectFor(to.path, authStore.isAuthenticated)
        if (redirect) return redirect
        if (to.name === 'native-login' && authStore.isAuthenticated) return { name: 'dashboard' }
    }

    const isLpVariant = typeof to.name === 'string' && to.name.startsWith('lp-')
    if (appShell && to.name && (NATIVE_EXCLUDED_ROUTE_NAMES.has(to.name as string) || isLpVariant)) {
        return authStore.isAuthenticated
            ? signedInHomeRoute(authStore.currentUser?.email)
            : { name: 'native-login' }
    }

    if (to.path === '/login' && authStore.isAuthenticated) {
        return appShell ? signedInHomeRoute(authStore.currentUser?.email) : { name: 'home' }
    }

    if (to.path === '/login' && appShell) {
        return { name: 'native-login' }
    }

    if (to.meta.authRequired && !authStore.isAuthenticated) {
        authStore.saveRedirectRoute(to)
        return { name: appShell ? 'native-login' : 'login' }
    }

    // Default post-login destination for allowlisted staff: harness, not Home.
    // Leave deep-linked dashboard URLs (section, query, OAuth code) alone.
    if (
        to.name === 'dashboard'
        && !to.params.section
        && Object.keys(to.query).length === 0
        && shouldLandOnDevHarness(authStore.currentUser?.email)
    ) {
        const fromLanding = !_from.name || [
            'home',
            'launch',
            'launch-signup',
            'login',
            'verify',
            'native-login',
            'inbox-landing',
            'deals-landing',
            'assistant-landing',
        ].includes(String(_from.name))
        if (fromLanding) {
            return { name: 'dev-testing' }
        }
    }

    if (to.meta.requireDevHarnessAccess) {
        const email = authStore.currentUser?.email as string | undefined
        if (!canAccessDevHarness(email)) {
            return { name: 'not-found' }
        }
    }

    return
}
